import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSubjectSelect,
} from '@/pages/education/components/EducationLookupSelects';

type UnitDetail = {
  unit: {
    id: string;
    subject_id: string;
    grade_level_id: string;
    sequence_no: number;
    code: string;
    name: string;
    description?: string | null;
    hours_hint?: number | null;
    is_active?: number | boolean;
  };
};

type FormValues = {
  subject_id: string;
  grade_level_id: string;
  sequence_no: number;
  code: string;
  name: string;
  description?: string;
  hours_hint?: number | null;
  is_active: boolean;
};

export default function CurriculumUnitFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.curriculum.manage') || user?.permissions?.includes('education.admin')
  );
  const isNew = !id || id === 'new';
  const [form] = Form.useForm<FormValues>();

  const detail = useQuery({
    queryKey: ['education-curriculum-unit', id],
    enabled: !isNew && Boolean(id),
    queryFn: async () => {
      const res = await api.get<UnitDetail>(`/education/curriculum/units/${id}`);
      return res.data;
    },
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ sequence_no: 1, is_active: true });
      return;
    }
    const unit = detail.data?.unit;
    if (!unit) return;
    form.setFieldsValue({
      subject_id: unit.subject_id,
      grade_level_id: unit.grade_level_id,
      sequence_no: unit.sequence_no,
      code: unit.code,
      name: unit.name,
      description: unit.description || undefined,
      hours_hint: unit.hours_hint ?? undefined,
      is_active: !(unit.is_active === 0 || unit.is_active === false),
    });
  }, [detail.data, form, isNew]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      isNew
        ? api.post<{ unit: { id: string } }>('/education/curriculum/units', values)
        : api.put(`/education/curriculum/units/${id}`, values),
    onSuccess: (res) => {
      message.success(isNew ? t('curriculumUnitCreated') : t('curriculumUnitUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-curriculum-units'] });
      const body = res.data as { unit?: { id?: string } };
      const nextId = body?.unit?.id || id;
      if (nextId) navigate(`/education/curriculum/units/${nextId}`);
      else navigate('/education/curriculum/units');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={isNew ? t('newCurriculumUnit') : t('editCurriculumUnit')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('newCurriculumUnit') : t('editCurriculumUnit')}
      description={t('curriculumUnitsIntro')}
      extra={
        <Button
          onClick={() =>
            navigate(isNew ? '/education/curriculum/units' : `/education/curriculum/units/${id}`)
          }
        >
          {t('cancel')}
        </Button>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }} loading={!isNew && detail.isLoading}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('curriculumNoMarksHint')} />
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="subject_id" label={t('subject')} rules={[{ required: true }]}>
            <EducationSubjectSelect style={{ width: '100%' }} disabled={!isNew} />
          </Form.Item>
          <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}>
            <EducationGradeSelect style={{ width: '100%' }} disabled={!isNew} />
          </Form.Item>
          <Form.Item name="sequence_no" label={t('sequence')} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('description', { defaultValue: 'Description' })}>
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
          <Form.Item name="hours_hint" label={t('hoursHint')}>
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          {!isNew ? (
            <Form.Item name="is_active" label={t('status')} rules={[{ required: true }]}>
              <Select
                options={[
                  { value: true, label: t('active') },
                  { value: false, label: t('inactive') },
                ]}
              />
            </Form.Item>
          ) : null}
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button
              onClick={() =>
                navigate(isNew ? '/education/curriculum/units' : `/education/curriculum/units/${id}`)
              }
            >
              {t('cancel')}
            </Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
